import React from 'react'
import PropTypes from 'prop-types'
import FilterItem from './FilterItem'
import { Menu, MenuList, MenuLabel } from '../../../bulma'

/**
* Sidebar filter component for Taxons.
*/
class TaxonFilter extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      // Stores the value that was most recently changed.
      changedValue: null,
    }

    this.getUpdatedValue = this.getUpdatedValue.bind(this)
    this.onClickHandler = this.onClickHandler.bind(this)
    this.renderTaxons = this.renderTaxons.bind(this)
  }

  componentWillReceiveProps(nextProps) {
    // If no longer loading, clear the `changedValue` so we no longer show
    // a loading indicator on that filter item.
    if (this.props.isLoading && !nextProps.isLoading) {
      this.setState({ changedValue: null })
    }
  }

  /**
  * Determines the new filter value based on the given `taxonId`. Also returns
  * an event name if `name` is given.
  *
  * @param taxonId {number|string} Taxon id to toggle.
  * @param name {string} optional name of taxon being toggled.
  * @returns {object} just the value if only `taxonId` given.
  */
  getUpdatedValue(taxonId, name) {
    const id = String(taxonId)
    let value = Array.isArray(this.props.value) ? this.props.value.map(v => String(v)) : this.props.value
    let action

    // Either add or remove the given id from the current list. If the current
    // filter is not already in a list, then format it so that it is a list.
    if (Array.isArray(value)) {
      if (value.indexOf(id) > -1) {
        value.splice(value.indexOf(id), 1)

        if (!value.length) {
          value = null
        }
        action = `Remove: ${name}`
      } else {
        value.push(id)
        action = `Add: ${name}`
      }
    } else if (value && value == id) {
      value = null
      action = `Remove: ${name}`
    } else {
      value = value ? [String(value), id] : [id]
      action = `Add: ${name}`
    }

    return name ? { value, action } : value
  }

  /**
  * onClick callback for when a taxon is clicked. Toggles the taxon's id
  * from the list of taxons, then calls the onChange callback with the updated
  * key and value for this filter.
  *
  * @param taxonId {string} taxon's id.
  * @param name {string} taxon's name.
  */
  onClickHandler(taxonId, name) {
    const { value, action } = this.getUpdatedValue(taxonId, name)

    // Record change so we know which item to show a loading indicator by.
    this.setState({ changedValue: String(taxonId) })

    this.props.onChange(value, action)
  }

  /**
  * Renders the list of taxons that the user can click on to toggle filters.
  */
  renderTaxons() {
    const {
      value, aggregations, getFallbackUrl, isLoading,
    } = this.props

    return aggregations
      .map((taxon) => {
        if (!taxon.taxon) {
          return false
        }
        const taxonId = String(taxon.taxon.id)
        const isSelected = value && (value == taxonId || value.indexOf(taxonId) > -1)
        const taxonIsLoading = isLoading && this.state.changedValue === taxonId
        const fallbackUrl = getFallbackUrl ? getFallbackUrl(this.getUpdatedValue(taxonId)) : null

        return (
          <FilterItem
            key={taxonId}
            onClick={() => this.onClickHandler(taxonId, taxon.taxon.name)}
            allowMultiple
            count={taxon.count}
            isDisabled={isLoading}
            isLoading={taxonIsLoading}
            isSelected={isSelected}
            fallbackUrl={fallbackUrl}
          >
            {taxon.taxon.name}
          </FilterItem>
        )
      })
  }

  render() {
    const {
      title, value, onChange, getFallbackUrl, aggregations, isLoading, ...rest
    } = this.props

    // Only load the taxon filter if there are taxons that can be filtered.
    if (!aggregations.length) return false

    return (
      <Menu {...rest}>
        {title ? <MenuLabel>{title}</MenuLabel> : false}
        <MenuList>
          {this.renderTaxons()}
        </MenuList>
      </Menu>
    )
  }
}

TaxonFilter.defaultProps = {
  aggregations: [],
  isLoading: false,
}

TaxonFilter.propTypes = {
  // Taxon aggregations.
  aggregations: PropTypes.array,
  // Filters/products currently being loaded.
  isLoading: PropTypes.bool,
  // Callback for when the taxon filter changes.
  onChange: PropTypes.func.isRequired,
  // Display title for this filter.
  title: PropTypes.string,
  // Current value for this filter.
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
}

export default TaxonFilter
